import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { ReactComponent as InfoIcon } from 'assets/info-icon.svg';
import { TransactionOptions, TransactionTypeOption } from '../types';

const MotionBox = motion(Box);

const collapseVariants = {
  hidden: {
    height: 0,
    opacity: 0,
    marginTop: 0,
    overflow: 'hidden' as const,
  },
  visible: {
    height: 'auto',
    opacity: 1,
    marginTop: 0,
    overflow: 'hidden' as const,
    transition: {
      height: { type: 'spring', stiffness: 300, damping: 30 },
      opacity: { duration: 0.25, delay: 0.08 },
    },
  },
  exit: {
    height: 0,
    opacity: 0,
    marginTop: 0,
    overflow: 'hidden' as const,
    transition: {
      height: { type: 'spring', stiffness: 300, damping: 30 },
      opacity: { duration: 0.15 },
    },
  },
};

interface StepExecutionSettingsProps {
  options: TransactionOptions;
  onChange: (options: TransactionOptions) => void;
  transactionType?: TransactionTypeOption;
}

const StepExecutionSettings = ({ options, onChange, transactionType }: StepExecutionSettingsProps) => {
  const isTokenAssociation = transactionType === 'TOKEN_ASSOCIATE';
  // Format date for datetime-local input
  const formatDateForInput = (date: Date | null): string => {
    if (!date) return '';
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60 * 1000);
    return localDate.toISOString().slice(0, 16);
  };

  // Parse date from datetime-local input
  const parseDateFromInput = (value: string): Date | null => {
    if (!value) return null;
    return new Date(value);
  };

  const minDate = new Date();
  minDate.setMinutes(minDate.getMinutes() + 5); // At least 5 minutes from now

  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 62); // Max 62 days (Hedera network limit)

  const isExpirationInvalid = options.expirationTime !== null && options.expirationTime < minDate;

  // For Token Association, expiration is required
  const isExpirationRequired = isTokenAssociation;
  const isExpirationMissing = isExpirationRequired && !options.expirationTime;

  return (
    <Flex direction="column" gap={6} w="full">
      {/* Token Association specific notice */}
      {isTokenAssociation && (
        <Box p={4} bg="blue.50" borderRadius={12} border="1px solid" borderColor="blue.200">
          <Flex align="center" gap={2} mb={2}>
            <InfoIcon />
            <Text size="sm" fontWeight={600} color="blue.700">
              Multi-Signature Token Association
            </Text>
          </Flex>
          <Text size="xs" color="blue.600" mb={2}>
            Token Association cannot use Hedera's scheduled transactions. Instead, signatures
            are collected manually before the execution time.
          </Text>
          <Text size="xs" color="blue.600" fontWeight={500}>
            How it works:
          </Text>
          <Box as="ul" pl={4} mt={1}>
            <Text as="li" size="xs" color="blue.600">
              Set an execution time below (when the transaction will be submitted)
            </Text>
            <Text as="li" size="xs" color="blue.600">
              Other signers must sign before this time
            </Text>
            <Text as="li" size="xs" color="blue.600">
              The transaction executes within 180 seconds of the execution time
            </Text>
          </Box>
        </Box>
      )}

      {/* Standard info box for other transaction types */}
      {!isTokenAssociation && (
        <Box p={4} bg="primary" borderRadius={12}>
          <Flex align="center" gap={2} mb={2}>
            <InfoIcon />
            <Text size="sm" fontWeight={500}>
              Execution Settings
            </Text>
          </Flex>
          <Text size="xs" color="gray.600">
            Configure when this scheduled transaction should expire and how it should execute.
            If left empty, the transaction will use the maximum expiration window (62 days).
          </Text>
        </Box>
      )}

      <Box>
        <FormControl isRequired={isExpirationRequired}>
          <FormLabel fontWeight={500}>
            {isTokenAssociation ? 'Execution Date & Time' : 'Expiration Date & Time'}
            {isExpirationRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
          </FormLabel>
          <Input
            h={12}
            borderRadius={16}
            type="datetime-local"
            value={formatDateForInput(options.expirationTime)}
            min={formatDateForInput(minDate)}
            max={formatDateForInput(maxDate)}
            isInvalid={isExpirationInvalid || isExpirationMissing}
            onChange={(e) => onChange({
              ...options,
              expirationTime: parseDateFromInput(e.target.value),
            })}
          />
          <AnimatePresence initial={false}>
            {isExpirationInvalid && (
              <MotionBox
                key="expiration-invalid"
                variants={collapseVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <Text color="red.500" size="xs" mt={1}>
                  {isTokenAssociation ? 'Execution time' : 'Expiration'} must be at least 5 minutes in the future
                </Text>
              </MotionBox>
            )}
          </AnimatePresence>
          <AnimatePresence initial={false}>
            {isExpirationMissing && (
              <MotionBox
                key="expiration-missing"
                variants={collapseVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <Text color="red.500" size="xs" mt={1}>
                  Execution time is required for token association
                </Text>
              </MotionBox>
            )}
          </AnimatePresence>
          {!isExpirationRequired && (
            <Text size="xs" color="gray.500" mt={1}>
              Leave empty for maximum expiration (62 days)
            </Text>
          )}
          {isTokenAssociation && (
            <Text size="xs" color="gray.500" mt={1}>
              All signers must sign before this time. Transaction executes within 180 seconds of this time.
            </Text>
          )}
        </FormControl>
      </Box>

      {/* Wait For Expiry toggle - only for scheduled transactions, not for Token Association */}
      {!isTokenAssociation && (
        <Box
          p={4}
          bg="white"
          borderRadius={16}
          border="1px solid"
          borderColor="gray.200"
        >
          <FormControl display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Flex align="center" gap={2}>
                <FormLabel mb={0} fontWeight={500}>
                  Wait For Expiry
                </FormLabel>
                <Tooltip
                  label="If enabled, the transaction will execute ONLY at the specific expiration time, even if all signatures are collected earlier. This is useful for time-locked transactions."
                  placement="top"
                  borderRadius={8}
                  maxW="300px"
                >
                  <InfoIcon cursor="pointer" />
                </Tooltip>
              </Flex>
              <Text size="xs" color="gray.500" mt={1}>
                Execute only at the scheduled time
              </Text>
            </Box>
            <Switch
              size="lg"
              colorScheme="green"
              isChecked={options.waitForExpiry}
              onChange={(e) => onChange({
                ...options,
                waitForExpiry: e.target.checked,
              })}
            />
          </FormControl>
        </Box>
      )}

      <AnimatePresence initial={false}>
        {!isTokenAssociation && options.waitForExpiry && !options.expirationTime && (
          <MotionBox
            key="wait-for-expiry-warning"
            variants={collapseVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Box p={3} bg="orange.50" borderRadius={8}>
              <Text size="xs" color="orange.700">
                Note: You've enabled "Wait For Expiry" but haven't set an expiration date.
                The transaction will wait the full 62 days before executing.
              </Text>
            </Box>
          </MotionBox>
        )}
      </AnimatePresence>
    </Flex>
  );
};

export default StepExecutionSettings;
